import { formatearMoneda, type Reporte, type TipoColumna } from '@barbones/shared';
import { Tabla, Td, Th, Tr } from '@/components/ui/data';
import type { KeyboardEvent } from 'react';
import { cn, fecha, fechaHora } from '@/lib/utils';

type Valor = string | number | null | undefined;
type FilaReporte = Reporte['filas'][number];

const NUMERICOS: TipoColumna[] = ['dinero', 'numero', 'porcentaje', 'duracion'];

/** Texto de una celda según el tipo de columna (mismo formato que el PDF). */
export function formatearCelda(tipo: TipoColumna, v: Valor): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'number') {
    switch (tipo) {
      case 'dinero':
        return formatearMoneda(v);
      case 'duracion':
        return `${Math.floor(v / 60)} min ${String(v % 60).padStart(2, '0')} s`;
      case 'porcentaje':
        return `${(v / 100).toFixed(1)} %`;
      default:
        return v.toLocaleString('es-MX');
    }
  }
  if (tipo === 'fechaHora') return Number.isNaN(Date.parse(v)) ? v : fechaHora(v);
  if (tipo === 'fecha' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return fecha(`${v}T12:00:00-06:00`);
  return v;
}

/**
 * Tabla de un reporte. Con `alAbrirFila` cada fila se abre con clic, Enter o Espacio
 * (p. ej. el detalle de una venta); `etiquetaFila` la nombra para lectores de pantalla.
 */
export function TablaReporte({
  reporte,
  alAbrirFila,
  etiquetaFila,
}: {
  reporte: Reporte;
  alAbrirFila?: (fila: FilaReporte) => void;
  etiquetaFila?: (fila: FilaReporte) => string;
}) {
  const { columnas, filas, totales } = reporte;
  const alinear = (tipo: TipoColumna) => (NUMERICOS.includes(tipo) ? 'cifras text-right' : '');
  return (
    <Tabla>
      <thead>
        <tr>
          {columnas.map((c) => (
            <Th key={c.clave} className={cn('whitespace-nowrap', NUMERICOS.includes(c.tipo) && 'text-right')}>
              {c.titulo}
            </Th>
          ))}
        </tr>
      </thead>
      <tbody>
        {filas.map((f, i) => (
          <Tr
            key={i}
            {...(alAbrirFila && {
              role: 'button',
              tabIndex: 0,
              'aria-label': etiquetaFila?.(f),
              onClick: () => alAbrirFila(f),
              onKeyDown: (e: KeyboardEvent<HTMLTableRowElement>) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();
                alAbrirFila(f);
              },
              className: 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/40',
            })}
          >
            {columnas.map((c) => (
              <Td
                key={c.clave}
                className={cn(alinear(c.tipo), c.tipo !== 'texto' && 'whitespace-nowrap', c.semaforo && cn('font-medium', Number(f[c.semaforo]) ? 'text-success' : 'text-destructive'))}
              >
                {formatearCelda(c.tipo, f[c.clave])}
              </Td>
            ))}
          </Tr>
        ))}
      </tbody>
      {totales && (
        <tfoot>
          <tr className="border-t-2 border-foreground/80 font-semibold">
            {columnas.map((c) => (
              <td key={c.clave} className={cn('px-3 py-2.5 text-[13.5px]', alinear(c.tipo))}>
                {totales[c.clave] === undefined ? '' : formatearCelda(c.tipo, totales[c.clave])}
              </td>
            ))}
          </tr>
        </tfoot>
      )}
    </Tabla>
  );
}
